import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router";
import { FaBuilding, FaUser, FaSearch, FaTimes } from "react-icons/fa";
import { useWorkspaces } from "@/api/account";
import { useWorkspace } from "@/hooks/use-workspace";
import { Workspace } from "@/models/account";

/**
 * WorkspacesSection component - displays workspaces in the sidebar.
 * Shows all workspaces with search functionality and allows switching between them.
 */
const WorkspacesSection: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: workspaces = [] } = useWorkspaces();
  const { currentWorkspace, selectWorkspace } = useWorkspace();

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filter workspaces by search
  const filteredWorkspaces = useMemo(() => {
    if (!searchQuery) return workspaces;
    const query = searchQuery.toLowerCase();
    return workspaces.filter(
      (ws) =>
        ws.name.toLowerCase().includes(query) ||
        ws.description?.toLowerCase().includes(query)
    );
  }, [workspaces, searchQuery]);

  // Sort: personal first, then by name
  const sortedWorkspaces = useMemo(() => {
    return [...filteredWorkspaces].sort((a, b) => {
      if (a.is_personal && !b.is_personal) return -1;
      if (!a.is_personal && b.is_personal) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [filteredWorkspaces]);

  // Handle workspace selection
  const handleSelectWorkspace = useCallback(
    (workspace: Workspace) => {
      selectWorkspace(workspace.id);
      // Navigate to workspaces page if not already there
      if (!location.pathname.startsWith("/workspaces")) {
        navigate("/workspaces");
      }
    },
    [selectWorkspace, navigate, location.pathname]
  );

  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchQuery("");
    searchInputRef.current?.focus();
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "f" && workspaces.length > 0) {
        if (searchInputRef.current) {
          e.preventDefault();
          searchInputRef.current.focus();
        }
      }
      if (e.key === "Escape" && isSearchFocused) {
        setSearchQuery("");
        searchInputRef.current?.blur();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isSearchFocused, workspaces.length]);

  if (workspaces.length === 0) {
    return (
      <div className="px-3 py-4 text-center text-[var(--arc-sidebar-text-secondary)]">
        <span className="text-xs">Aucun workspace</span>
      </div>
    );
  }

  return (
    <div className="px-3 py-2">
      {/* Section Title */}
      <div className="flex items-center gap-2 px-2 mb-2 text-[var(--arc-accent)]">
        <FaBuilding size={10} />
        <span className="text-[10px] font-semibold uppercase tracking-wider">
          Workspaces
        </span>
        <div className="flex-1 h-px bg-[var(--arc-sidebar-border-accent)]" />
        <span className="text-[10px] text-[var(--arc-sidebar-text-secondary)]">
          {filteredWorkspaces.length}
        </span>
      </div>

      {/* Search Bar */}
      {workspaces.length > 3 && (
        <div className="px-1 mb-3">
          <div
            className={`relative flex items-center rounded-md transition-all duration-200 ${
              isSearchFocused
                ? "bg-[var(--arc-sidebar-accent-glow)] ring-1 ring-[var(--arc-accent)]"
                : "bg-[var(--arc-sidebar-accent-glow)] hover:bg-[rgba(var(--arc-accent-rgb),0.15)]"
            }`}
          >
            <FaSearch
              className={`absolute left-2.5 transition-colors duration-200 ${
                isSearchFocused ? "arc-sidebar-icon-active" : ""
              }`}
              size={11}
            />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              placeholder="Rechercher..."
              className="w-full bg-transparent text-[var(--arc-sidebar-text-primary)] text-xs py-1.5 pl-7 pr-7 placeholder-[var(--arc-sidebar-text-secondary)] focus:outline-none focus:ring-0"
            />
            {searchQuery && (
              <button
                onClick={handleClearSearch}
                className="absolute right-2 p-0.5 rounded hover:bg-[var(--arc-sidebar-accent-glow)] transition-colors text-[var(--arc-sidebar-text-secondary)] hover:text-[var(--arc-sidebar-text-primary)]"
              >
                <FaTimes size={10} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Workspaces List */}
      <div className="space-y-0.5">
        {sortedWorkspaces.map((workspace) => {
          const isActive = currentWorkspace?.id === workspace.id;
          const Icon = workspace.is_personal ? FaUser : FaBuilding;
          
          return (
            <button
              key={workspace.id}
              onClick={() => handleSelectWorkspace(workspace)}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left transition-all ${
                isActive
                  ? "bg-[var(--arc-sidebar-accent-glow)] text-[var(--arc-sidebar-text-primary)]"
                  : "text-[var(--arc-sidebar-text-secondary)] hover:bg-[var(--arc-sidebar-accent-glow)] hover:text-[var(--arc-sidebar-text-primary)]"
              }`}
            >
              <Icon
                size={10}
                className={isActive ? "text-[var(--arc-accent)]" : workspace.is_personal ? "text-accent" : "text-warning"}
              />
              <span className="text-xs truncate flex-1">{workspace.name}</span>
              {workspace.is_personal && (
                <span className="text-[8px] px-1 py-0.5 bg-accent/20 text-accent rounded">
                  Personal
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* No results */}
      {filteredWorkspaces.length === 0 && searchQuery && (
        <div className="px-2 py-3 text-center text-[var(--arc-sidebar-text-secondary)]">
          <span className="text-xs">Aucun résultat</span>
        </div>
      )}
    </div>
  );
};

WorkspacesSection.displayName = "WorkspacesSection";

export default WorkspacesSection;
